import React from 'react'
import type { Catalog, Entry, FeatureKey, KV } from './types'
import { FEATURE_ORDER, featuresToMap } from './types'
import { AVAILABLE_OPTIONS } from './availableOptions'

// Environment variables
const GITHUB_TOKEN = import.meta.env.VITE_GITHUB_TOKEN
const GITHUB_USERNAME = import.meta.env.VITE_GITHUB_USERNAME || 'TianyiQ'
const GITHUB_REPO = import.meta.env.VITE_GITHUB_REPO || 'Eval-Reasoning-Consistency'
const DISABLE_LOCAL = import.meta.env.VITE_DISABLE_LOCAL === 'true'

export type LoadedData = {
  all: Entry[]
  runs: Entry[]
  artifacts: Entry[]
  // Features → values (non-null) sorted by global frequency
  featureValues: Record<FeatureKey, { value: string; count: number }[]>
  // Inverted index for fast filtering
  index: Record<FeatureKey, Record<string, number[]>>
  // Indices for entries with null feature values
  nullIndex: Record<FeatureKey, number[]>
  // Per-entry normalized feature values: null or array of strings (union semantics)
  entryFeatures: Array<Record<FeatureKey, string[] | null>>
  // Path token frequency cache used for highlighting
  pathTokenFreq?: Map<string, number>
}

function isNonNull(v: any): boolean {
  return v !== null && v !== undefined
}

// Utility function to convert file paths to GitHub API URLs
export function getGitHubApiUrl(path: string): string {
  return `https://api.github.com/repos/${GITHUB_USERNAME}/${GITHUB_REPO}/contents/${path}`
}

// Utility function to convert file paths to GitHub raw URLs (fallback)
export function getGitHubRawUrl(path: string): string {
  return `https://raw.githubusercontent.com/${GITHUB_USERNAME}/${GITHUB_REPO}/main/${path}`
}

// Unified function to get the best available URL for any file type
export function getBestUrl(filePath: string, preferLocal: boolean = false): string {
  console.log('🔍 getBestUrl called with:', { filePath, preferLocal, DISABLE_LOCAL })
  
  // If local access is not disabled, use local URLs
  if (!DISABLE_LOCAL) {
    const cleanPath = filePath.startsWith('data/') ? filePath.substring(5) : filePath
    const localUrl = `/data/${cleanPath}`
    console.log('💾 Local access enabled, returning local URL:', localUrl)
    return localUrl
  }
  
  // If local access is disabled, use GitHub raw URLs
  const githubUrl = getGitHubRawUrl(filePath)
  console.log('🚫 Local access disabled, returning GitHub URL:', githubUrl)
  return githubUrl
}

import { unifiedFetch, DetailedFetchError } from './unifiedFetch'

// This function has been replaced by unifiedFetch in unifiedFetch.ts
// Keeping stub for backwards compatibility
export async function fetchFromGitHub(path: string): Promise<string> {
  const result = await unifiedFetch(path)
  if (typeof result.content === 'string') {
    return result.content
  }
  throw new Error('fetchFromGitHub called with binary file')
}

// Process the raw catalog data into the format needed by the app
function processCatalog(catalog: Catalog): LoadedData {
  const runs = (catalog.runs || []).filter(Boolean)
  const artifacts = (catalog.artifacts || []).filter(Boolean)
  const all = [...runs, ...artifacts]

  // Normalize per-entry feature values as union lists
  const entryFeatures: Array<Record<FeatureKey, string[] | null>> = []
  // Collect feature frequencies
  const freq: Record<FeatureKey, Map<string, number>> = Object.create(null)
  for (const k of FEATURE_ORDER) freq[k] = new Map()

  let resultAnalyzerCount = 0
  for (const e of all) {
    const m = featuresToMap(e.features || []) as Record<string, any>
    const record = {} as Record<FeatureKey, string[] | null>
    for (const k of FEATURE_ORDER) {
      const v = m[k]
      if (!isNonNull(v)) {
        record[k] = null
        continue
      }
      if (Array.isArray(v)) {
        const arr = v.map((x: any) => String(x))
        record[k] = arr
        for (const s of arr) freq[k].set(s, (freq[k].get(s) || 0) + 1)
        // Debug logging for Result Analyzer
        if (k === 'Result Analyzer') {
          resultAnalyzerCount++
          if (resultAnalyzerCount <= 5) {
            console.log(`[processCatalog] Entry with Result Analyzer array:`, arr)
          }
        }
      } else {
        const s = String(v)
        record[k] = [s]
        freq[k].set(s, (freq[k].get(s) || 0) + 1)
      }
    }
    entryFeatures.push(record)
  }
  console.log(`[processCatalog] Total entries with Result Analyzer: ${resultAnalyzerCount}`)
  console.log(`[processCatalog] Result Analyzer frequency map:`, freq['Result Analyzer'])

  // Combine available options with actual data frequencies
  const featureValues = Object.fromEntries(
    FEATURE_ORDER.map(k => {
      // Get frequencies from actual data
      const dataFreqMap = freq[k]

      // Get all available options for this feature
      const availableOptions = AVAILABLE_OPTIONS[k] || []

      // Create a combined list with all available options
      const combinedOptions = new Map<string, number>()

      // First, add all available options with 0 count
      for (const option of availableOptions) {
        combinedOptions.set(option, 0)
      }

      // Then, update with actual counts from data
      for (const [value, count] of dataFreqMap) {
        combinedOptions.set(value, count)
      }

      // Convert to array and sort
      const arr = Array.from(combinedOptions.entries()).map(([value, count]) => ({ value, count }))
      arr.sort((a, b) => {
        // First sort by count (descending), then alphabetically
        if (b.count !== a.count) return b.count - a.count
        return a.value.localeCompare(b.value)
      })

      return [k, arr]
    })
  ) as LoadedData['featureValues']

  // Build inverted index: for each feature value, list of entry indices
  const index: LoadedData['index'] = Object.create(null)
  const nullIndex: LoadedData['nullIndex'] = Object.create(null)
  for (const k of FEATURE_ORDER) {
    index[k] = Object.create(null)
    nullIndex[k] = []
  }
  entryFeatures.forEach((rec, idx) => {
    for (const k of FEATURE_ORDER) {
      const vals = rec[k]
      if (vals === null) {
        nullIndex[k].push(idx)
      } else {
        for (const s of vals) {
          if (!index[k][s]) index[k][s] = []
          index[k][s].push(idx)
        }
      }
    }
  })

  return { all, runs, artifacts, featureValues, index, nullIndex, entryFeatures }
}

// Test function to verify data source accessibility using unified fetch
export async function testGitHubAccess(): Promise<{ accessible: boolean; status?: number; error?: string; source: 'github' | 'local' | 'neither'; config: { tokenConfigured: boolean; localDisabled: boolean } }> {
  const config = {
    tokenConfigured: !!GITHUB_TOKEN,
    localDisabled: DISABLE_LOCAL
  }
  
  console.log('Testing access configuration:', config)
  
  try {
    // Try to fetch the catalog using the unified system
    const catalogPath = 'data/scratchpad/results_catalog_populated.json'
    const result = await unifiedFetch(catalogPath)
    
    // Map the source to the expected format
    let source: 'github' | 'local' | 'neither' = 'neither'
    if (result.source === 'local') {
      source = 'local'
    } else if (result.source.includes('github') || result.source.includes('hf')) {
      source = 'github' // Consider both HF and GitHub as external sources
    }
    
    console.log(`✅ Successfully accessed catalog from ${result.source}`)
    return { accessible: true, source, config }
    
  } catch (error) {
    console.error('❌ Failed to access catalog:', error)
    
    if (error instanceof DetailedFetchError) {
      // Extract details from the attempts
      const lastAttempt = error.attempts[error.attempts.length - 1]
      return { 
        accessible: false, 
        status: lastAttempt?.status,
        error: `Failed after ${error.attempts.length} attempts: ${error.message}`,
        source: 'neither', 
        config 
      }
    }
    
    return { 
      accessible: false, 
      error: error instanceof Error ? error.message : String(error),
      source: 'neither', 
      config 
    }
  }
}

export async function loadCatalog(): Promise<LoadedData> {
  console.log('📦 Loading catalog using unified fetch system...')
  
  try {
    // Use the unified fetch system for catalog loading
    // Note: For local access, we need a special path for the catalog
    // since it might be at /results_catalog_populated.json in public folder
    // rather than at /data/scratchpad/results_catalog_populated.json
    const catalogPath = 'data/scratchpad/results_catalog_populated.json'
    
    const result = await unifiedFetch(catalogPath)
    
    if (typeof result.content !== 'string') {
      throw new Error('Catalog fetch returned binary content instead of JSON text')
    }
    
    // Parse the JSON content
    let catalog: Catalog
    try {
      catalog = JSON.parse(result.content)
    } catch (parseError) {
      // Check if we got HTML by mistake
      if (result.content.includes('<!doctype html>') || result.content.includes('<div id=root>')) {
        throw new Error('Received HTML index page instead of catalog JSON. The catalog file may not exist at the expected location.')
      }
      throw new Error(`Failed to parse catalog JSON: ${parseError instanceof Error ? parseError.message : String(parseError)}`)
    }
    
    // Validate that we got a valid catalog
    if (!catalog || typeof catalog !== 'object') {
      throw new Error('Invalid catalog format: expected an object')
    }
    
    console.log(`✅ Successfully loaded catalog from ${result.source} with ${catalog.runs?.length || 0} runs and ${catalog.artifacts?.length || 0} artifacts`)
    
    return processCatalog(catalog)
  } catch (error) {
    console.error('❌ Catalog loading failed:', error)
    
    // Provide helpful error message based on the error type
    if (error instanceof DetailedFetchError) {
      const sources = error.attempts.map(a => a.method).join(', ')
      throw new Error(`Failed to load catalog from any source (tried: ${sources}). ${error.message}`)
    }
    
    throw error
  }
}

export type Filters = Partial<Record<FeatureKey, Set<string>>>

export function parseFiltersFromSearch(search: string): Filters {
  const params = new URLSearchParams(search)
  const f: Filters = {}
  const map: Record<string, FeatureKey> = {
    es: 'Evaluation Strategy',
    dom: 'Domain',
    mode: 'Reasoning Mode',
    model: 'Model',
    sp: 'System Prompt',
    ra: 'Result Analyzer',  // Added Result Analyzer mapping
  }
  for (const key in map) {
    const v = params.get(key)
    if (v) f[map[key]] = new Set(v.split(','))
  }
  console.log('[parseFiltersFromSearch] Parsed filters:', f)
  return f
}

export function filtersToSearch(filters: Filters): string {
  const params = new URLSearchParams()
  const map: [FeatureKey, string][] = [
    ['Evaluation Strategy', 'es'],
    ['Domain', 'dom'],
    ['Reasoning Mode', 'mode'],
    ['Model', 'model'],
    ['System Prompt', 'sp'],
    ['Result Analyzer', 'ra'],  // Added Result Analyzer mapping
  ]
  for (const [fk, qk] of map) {
    const s = filters[fk]
    if (s && s.size > 0) params.set(qk, Array.from(s).join(','))
  }
  const qs = params.toString()
  console.log('[filtersToSearch] Generated search string:', qs)
  return qs ? `?${qs}` : ''
}

export function computeActiveIndices(data: LoadedData, filters: Filters): number[] {
  // If no rows are selected, all entries are active
  const rows = (Object.keys(filters) as FeatureKey[]).filter(k => (filters[k]?.size || 0) > 0)
  if (rows.length === 0) return data.all.map((_, i) => i)

  let current: Set<number> | null = null
  for (const fk of rows) {
    const selected = filters[fk]!
    const union = new Set<number>()
    for (const val of selected) {
      const arr = data.index[fk][val] || []
      for (const idx of arr) union.add(idx)
    }
    // Null matching rule: if two or more buttons are pushed on that row, null entries also match
    if (selected.size >= 2) {
      for (const idx of data.nullIndex[fk]) union.add(idx)
    }

    if (current === null) current = union
    else {
      const next = new Set<number>()
      for (const idx of current) if (union.has(idx)) next.add(idx)
      current = next
    }
    if (current.size === 0) break
  }
  return Array.from(current || [])
}

// Optional: compute per-token frequencies for a set of paths to enable highlighting of rare tokens
export function buildPathTokenFrequency(paths: string[], type: 'runs' | 'artifacts'): Map<string, number> {
  const freq = new Map<string, number>()
  for (const p of paths) {
    const parts = p.split(/[\/]/).flatMap(seg => seg.split(/-/))
    const seen = new Set<string>()
    for (const t of parts) {
      const token = t.trim()
      if (!token) continue
      if (seen.has(token)) continue
      seen.add(token)
      freq.set(token, (freq.get(token) || 0) + 1)
    }
  }
  return freq
}

export function renderHighlightedPath(p: string, freq: Map<string, number>, totalPaths: number): JSX.Element {
  const segments = p.split('/')
  const rendered: React.ReactNode[] = []
  segments.forEach((seg, i) => {
    const tokens = seg.split('-')
    const tokenNodes: React.ReactNode[] = []
    tokens.forEach((tk, j) => {
      const count = freq.get(tk.trim()) || 0
      // Highlight if token appears in no more than half of the paths in this view
      const rare = count <= Math.max(1, Math.floor(totalPaths / 2))
      const style: React.CSSProperties = { fontWeight: rare ? 800 : 500, color: rare ? '#6366f1' : undefined }
      tokenNodes.push(React.createElement('span', { key: `t-${i}-${j}`, style }, tk))
      if (j < tokens.length - 1) tokenNodes.push(React.createElement('span', { key: `td-${i}-${j}` }, '-'))
    })
    rendered.push(React.createElement('span', { key: `seg-${i}` }, tokenNodes))
    if (i < segments.length - 1) rendered.push(React.createElement('span', { key: `sd-${i}` }, '/'))
  })
  return React.createElement(React.Fragment, null, rendered) as unknown as JSX.Element
}

export function wouldButtonActivateAnything(
  data: LoadedData,
  filters: Filters,
  feature: FeatureKey,
  value: string
): boolean {
  const newFilters: Filters = { ...filters }
  const set = new Set<string>(filters[feature] || [])
  if (!set.has(value)) set.add(value)
  newFilters[feature] = set
  const active = computeActiveIndices(data, newFilters)
  return active.length > 0
}


